// Utility functions for status page

/**
 * Normalizes status values to consistent format
 * @param {string|number} status - Raw status value
 * @returns {string} Normalized status
 */
export function normalizeStatus(status) {
  if (!status) return 'Unknown';
  
  const statusStr = String(status).toLowerCase();
  
  // Map various status formats to standard values
  if (statusStr === 'healthy' || statusStr === 'operational' || statusStr === '200' || statusStr === 'up') {
    return 'Operational';
  }
  if (statusStr === 'unhealthy' || statusStr === 'down' || statusStr === 'error') {
    return 'Major Outage';
  }
  if (statusStr === 'degraded' || statusStr === 'warning') {
    return 'Degraded Performance';
  }
  
  // Capitalize first letter for display
  return statusStr.charAt(0).toUpperCase() + statusStr.slice(1);
}

/**
 * Gets status color class
 * @param {string} status - Normalized status
 * @returns {string} CSS class name
 */
export function getStatusColorClass(status) {
  const normalized = normalizeStatus(status).toLowerCase().replace(/\s+/g, '-');
  return `status-${normalized}`;
}

/**
 * Gets overall system status based on services
 * @param {Array} services - Array of service objects with status
 * @returns {string} Overall status
 */
export function getOverallStatus(services) {
  if (!services || services.length === 0) return 'Unknown';
  
  const statuses = services.map(s => normalizeStatus(s.status));
  
  if (statuses.some(s => s === 'Major Outage')) return 'Major Outage';
  if (statuses.some(s => s === 'Degraded Performance')) return 'Degraded Performance';
  if (statuses.every(s => s === 'Operational')) return 'Operational';
  
  return 'Partial Outage';
}

/**
 * Formats timestamp to readable date
 * @param {Date|string|number} timestamp - Timestamp to format
 * @returns {string} Formatted date string
 */
export function formatTimestamp(timestamp) {
  if (!timestamp) return 'Unknown';
  
  try {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  } catch (e) {
    return 'Invalid Date';
  }
}

/**
 * Retry a promise-returning function
 * @param {Function} fn - Function to retry
 * @param {number} retries - Number of retries
 * @param {number} delay - Delay between retries in ms
 * @returns {Promise} Result of the function
 */
export async function retry(fn, retries = 3, delay = 1000) {
  try {
    return await fn();
  } catch (error) {
    if (retries <= 0) throw error;
    await new Promise(resolve => setTimeout(resolve, delay));
    return retry(fn, retries - 1, delay);
  }
}

